import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { registerForPushNotifications } from '../lib/fcm';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session lives in an httpOnly cookie (XSS-safe): every request must send it.
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* cookie may already be gone */ }
    setUser(null);
    // Migration cleanup: drop any legacy token from older sessions
    try { localStorage.removeItem('triflow_token'); } catch { /* noop */ }
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  useEffect(() => {
    const restoreSession = async () => {
      // Migration path: sessions created before the httpOnly-cookie switch
      // only have the legacy localStorage token. Use it once so the user
      // isn't logged out by the upgrade; it's removed on next login/logout.
      let legacy = null;
      try { legacy = localStorage.getItem('triflow_token'); } catch { /* noop */ }
      if (legacy) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${legacy}`;
      }
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        // Register the device for FCM push. No-op on the web build (only
        // fires inside the Capacitor Android wrapper).
        registerForPushNotifications();
      } catch {
        try { localStorage.removeItem('triflow_token'); } catch { /* noop */ }
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    restoreSession();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { user: userData } = response.data;
    // Cookie set by the server; never persist the token in JS-accessible storage
    try { localStorage.removeItem('triflow_token'); } catch { /* noop */ }
    delete axios.defaults.headers.common['Authorization'];
    setUser(userData);
    registerForPushNotifications();
    return userData;
  };

  const register = async (name, email, password, inviteCode) => {
    const payload = { name, email, password };
    if (inviteCode && inviteCode.trim()) payload.invite_code = inviteCode.trim().toUpperCase();
    const response = await axios.post(`${API}/auth/register`, payload);
    const { user: userData } = response.data;
    try { localStorage.removeItem('triflow_token'); } catch { /* noop */ }
    delete axios.defaults.headers.common['Authorization'];
    setUser(userData);
    return userData;
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
